'use client';

import { Button } from '@workspace/ui/components/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@workspace/ui/components/command';
import { Input } from '@workspace/ui/components/input';
import { Label } from '@workspace/ui/components/label';
import { Popover, PopoverContent, PopoverTrigger } from '@workspace/ui/components/popover';
import { Skeleton } from '@workspace/ui/components/skeleton';
import { cn } from '@workspace/ui/lib';
import { ChevronDown } from 'lucide-react';
import Image from 'next/image';
import { forwardRef, useState } from 'react';
import { NumericFormat, type NumericFormatProps } from 'react-number-format';
import { z } from 'zod';

// import { useTranslations } from "next-intl";

interface AmountInputValue {
  value: string;
  currency: string;
}

type Currency = { code: string; id?: number; flag?: string; name?: string };

interface AmountInputProps extends Omit<NumericFormatProps, 'value' | 'onChange' | 'size'> {
  label?: string;
  value: AmountInputValue;
  inModal?: boolean;
  max?: number;
  onChange: (value: AmountInputValue) => void;
  onBlur?: () => void;
  showCurrencyFlag?: boolean;
  fixedCurrency?: boolean;
  'aria-invalid'?: boolean;
  disabled?: boolean;
  className?: string;
  currencyClassName?: string;
  isAmountPending?: boolean;
  placeholder?: string;
  availableBalance?: {
    amount: string;
    currency: string;
  };
  readOnly?: boolean;
  variant?: 'sm' | 'lg';
  /**
   * Represents a list of currencies that can be used in the AmountInput component.
   * Each currency object can optionally have an `id` property.
   *
   * If the `id` property is present, the value saved from the selection will be the `id`.
   * If the `id` property is not present, the value saved from the selection will be the currency code.
   */
  currencies: Currency[];
  /**
   * Whether to show the search input in the currency selector dropdown.
   * Defaults to true. Set to false to hide the search (useful when there are few currencies).
   */
  showSearch?: boolean;
}

export function getAmountSchema(t: (key: string) => string) {
  const amountSchema = z.object({
    value: z.string().min(1, t('amountIsRequired')),
    currency: z.string().min(1, t('currencyIsRequired')),
  });

  return amountSchema;
}

const getCurrencyValue = (value: string, currencies: Currency[]) => {
  const [identifier] = value.split('-');
  const currency = currencies.find((c) => c.id?.toString() === identifier || c.code === identifier);
  return currency ?? { code: identifier || '' };
};

export const AmountInput = forwardRef<HTMLDivElement, AmountInputProps>(
  (
    {
      label,
      inModal = false,
      value,
      onChange,
      onBlur,
      fixedCurrency = true,
      showCurrencyFlag = true,
      'aria-invalid': ariaInvalid,
      disabled,
      currencies,
      className,
      currencyClassName,
      isAmountPending = false,
      placeholder = '0.00',
      availableBalance,
      max,
      variant = 'lg',
      readOnly = false,
      showSearch = true,
      ...props
    },
    ref
  ) => {
    // const tGlobal = useTranslations("global");
    // const tSearchPlaceholder = tGlobal("searchPlaceholder");
    // const tAmountInput = useTranslations("amountInput");
    // const tAvailableBalance = tAmountInput("availableBalance");
    // const tEnterAmount = tAmountInput("enterAmount");

    const flag = currencies?.find(
      (currentCurrency) => currentCurrency.code === value.currency
    )?.flag;

    const [currencyOpen, setCurrencyOpen] = useState(false);

    const isSmall = variant === 'sm';

    return (
      <div className={cn('flex flex-col', availableBalance && 'gap-2')}>
        <div
          onBlur={onBlur}
          data-readonly={readOnly ? 'true' : undefined}
          className={cn(
            'border-grey-300 dark:border-grey-700 relative flex w-full flex-col',
            isSmall && !readOnly && 'h-12 rounded-2xl px-4 py-3',
            !isSmall && !readOnly && 'rounded-[32px] px-6 py-[22px] md:px-8',
            !readOnly && 'border-grey-300 dark:border-grey-700 border',
            !disabled &&
              !ariaInvalid &&
              'focus-within:border-neutral-200 hover:border-neutral-200 dark:focus-within:border-neutral-100 dark:hover:border-neutral-100',
            disabled ? 'border-grey-400 dark:border-grey-700' : '',
            readOnly &&
              'border-grey-300 dark:border-grey-700 hover:border-grey-300 dark:hover:border-grey-700 rounded-2xl border px-4 py-3',
            ariaInvalid && 'border-red-500 dark:border-red-400',
            className
          )}
          ref={ref}
        >
          {!isSmall && !readOnly && (
            <Label
              htmlFor="amount"
              className="text-grey-700 dark:text-grey-500 text-sm font-normal"
            >
              {label || 'Enter amount'}
            </Label>
          )}
          {isAmountPending ? (
            <Skeleton
              className={cn(
                'bg-grey-400 dark:bg-grey-700',
                isSmall ? 'mt-0.5 h-5 w-[50px]' : 'mt-1 mb-1 h-10 w-[90px]'
              )}
            />
          ) : (
            <div className={cn(readOnly && 'flex items-center gap-2')}>
              {readOnly && showCurrencyFlag && flag && value.currency && (
                <Image
                  src={flag}
                  alt={value.currency}
                  width={32}
                  height={32}
                  className={cn(isSmall ? 'h-5 w-5' : 'mt-1.5 h-6 w-6 md:h-8 md:w-8')}
                />
              )}
              {readOnly ? (
                <span
                  className={cn(
                    'mt-3 flex items-center text-base leading-tight font-normal text-neutral-200 dark:text-neutral-100',
                    !readOnly ? 'h-12' : 'text-grey-600 dark:text-grey-600',
                    isSmall && 'mt-0'
                  )}
                >
                  {value.value} {value.currency}
                </span>
              ) : (
                <NumericFormat
                  {...props}
                  readOnly={readOnly}
                  customInput={Input}
                  value={value.value ?? undefined}
                  id="amount"
                  name="amount"
                  isAllowed={(values) => {
                    if (max) {
                      return Number(values.value) <= max;
                    }
                    return true;
                  }}
                  onValueChange={(values) => {
                    onChange({ ...value, value: values.value });
                  }}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData('text').trim();
                    // Detect European decimal notation where comma is the decimal separator
                    // e.g. "34875,46" or "34.875,46" — comma followed by 1–2 digits at end
                    if (/^[\d.]+,\d{1,2}$/.test(pasted)) {
                      e.preventDefault();
                      const normalized = pasted.replace(/\./g, '').replace(',', '.');
                      if (max && Number(normalized) > max) return;
                      onChange({ ...value, value: normalized });
                    }
                  }}
                  thousandSeparator=","
                  decimalScale={2}
                  disabled={disabled}
                  fixedDecimalScale={!!value.value}
                  aria-invalid={ariaInvalid}
                  className={cn(
                    'rounded-none border-0 p-0 shadow-none',
                    disabled && 'text-grey-400! dark:text-grey-700!',
                    showCurrencyFlag &&
                      !fixedCurrency &&
                      'w-[calc(100%-110px)] md:w-[calc(100%-110px)]',
                    showCurrencyFlag &&
                      fixedCurrency &&
                      'w-[calc(100%-76px)] md:w-[calc(100%-80px)]',
                    !showCurrencyFlag &&
                      !fixedCurrency &&
                      'w-[calc(100%-80px)] md:w-[calc(100%-70px)]',
                    !showCurrencyFlag &&
                      fixedCurrency &&
                      'w-[calc(100%-50px)] md:w-[calc(100%-45px)]',
                    isSmall
                      ? 'mt-0.5 h-fit text-base leading-tight font-normal'
                      : 'text-2xl leading-loose font-medium md:text-3xl md:leading-10'
                  )}
                  placeholder={placeholder}
                  maxLength={16}
                />
              )}
            </div>
          )}
          {!readOnly && (
            <div
              className={cn(
                'absolute inset-y-0 right-0 flex items-center',
                isSmall ? 'pr-4' : 'pr-8',
                currencyClassName
              )}
            >
              {fixedCurrency ? (
                <CurrencyDisplay
                  disabled={disabled}
                  showCurrencyFlag={showCurrencyFlag}
                  currency={value.currency}
                  flag={
                    currencies?.find((currentCurrency) => currentCurrency.code === value.currency)
                      ?.flag
                  }
                  size={variant}
                />
              ) : (
                <Popover open={currencyOpen} onOpenChange={setCurrencyOpen} modal={inModal}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="input"
                      size="input"
                      role="combobox"
                      className="group w-full border-none p-0"
                    >
                      <CurrencyDisplay
                        disabled={disabled}
                        showCurrencyFlag={showCurrencyFlag}
                        flag={getCurrencyValue(value.currency, currencies)?.flag}
                        currency={
                          getCurrencyValue(value.currency, currencies)?.code ||
                          value.currency.split('-')[0] ||
                          ''
                        }
                        size={variant}
                      />
                      <ChevronDown
                        className={cn(
                          'h-5 w-5 text-neutral-200 group-data-[state=open]:rotate-180 dark:text-neutral-100',
                          disabled && 'text-grey-400 dark:text-grey-700'
                        )}
                      />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="end"
                    className="w-[290px] max-w-[290px] min-w-[290px] overflow-y-scroll p-4"
                  >
                    <Command>
                      {showSearch && <CommandInput placeholder={'Search'} />}
                      <CommandList className="max-h-[240px] w-full overflow-y-scroll">
                        <CommandEmpty />
                        <CommandGroup className="flex flex-col px-0.5 py-4">
                          {currencies?.map((option, index: number) => (
                            <CommandItem
                              key={option.id ?? option.code}
                              value={
                                option.id?.toString()
                                  ? `${option.id.toString()}-${option.code}-${option.name}`
                                  : `${option.code}-${option.name}`
                              }
                              onSelect={(currentValue: string) => {
                                onChange({ ...value, currency: currentValue });
                                setCurrencyOpen(false);
                              }}
                              className={cn(
                                'mr-2 flex h-12 cursor-pointer justify-between gap-2 rounded-2xl pr-3',
                                'hover:bg-grey-25 hover:dark:bg-grey-900 dark:text-grey-400 hover:font-medium hover:text-neutral-200',
                                index !== currencies.length - 1 && 'mb-2',
                                getCurrencyValue(value.currency, currencies).code === option.code &&
                                  'bg-grey-25 dark:bg-grey-900 dark:text-grey-400 font-medium text-neutral-200'
                              )}
                            >
                              <div className="flex items-center gap-2">
                                <CurrencyDisplay
                                  disabled={disabled}
                                  showCurrencyFlag={showCurrencyFlag}
                                  currency={option.code}
                                  flag={option.flag}
                                  size={variant}
                                />
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          )}
        </div>
        {availableBalance && (
          <span
            className={cn(
              'text-xs font-normal',
              disabled ? 'text-grey-400 dark:text-grey-700' : 'text-grey-700 dark:text-grey-500'
            )}
          >
            {'Available balance'}:{' '}
            <span
              className={cn(
                'text-xs font-bold',
                disabled ? 'text-grey-400 dark:text-grey-700' : 'dark:text-grey-300'
              )}
            >
              {availableBalance.amount} {availableBalance.currency}
            </span>
          </span>
        )}
      </div>
    );
  }
);

AmountInput.displayName = 'AmountInput';

interface CurrencyDisplayProps {
  showCurrencyFlag: boolean;
  currency: string;
  disabled?: boolean;
  flag?: string;
  size?: 'sm' | 'lg';
}

const CurrencyDisplay = ({
  showCurrencyFlag,
  currency,
  flag,
  disabled,
  size = 'lg',
}: CurrencyDisplayProps) => {
  const isSmall = size === 'sm';
  const hasCurrency = Boolean(currency.trim());
  const displayCurrency = hasCurrency ? currency : 'Currency';

  return (
    <div
      className={cn(
        'flex h-fit w-fit items-center justify-end',
        isSmall ? 'gap-2' : 'gap-2 md:gap-3'
      )}
    >
      {disabled && !isSmall && (
        <div
          className={cn(
            'bg-grey-400 dark:bg-grey-700 rounded-full',
            isSmall ? 'h-5 w-5' : 'h-6 w-6 md:h-8 md:w-8'
          )}
        />
      )}
      {((!isSmall && !disabled) || isSmall) && showCurrencyFlag && flag && (
        <Image
          src={flag}
          alt={currency}
          width={32}
          height={32}
          className={cn(isSmall ? 'h-5 w-5' : 'h-6 w-6 md:h-8 md:w-8')}
        />
      )}
      <span
        className={cn(
          'w-fit font-normal',
          isSmall ? 'text-base leading-tight' : 'text-lg',
          disabled
            ? 'text-grey-400 dark:text-grey-700'
            : hasCurrency
              ? 'text-neutral-200 dark:text-neutral-100'
              : 'text-grey-500 dark:text-grey-500'
        )}
      >
        {disabled && !isSmall ? '---' : displayCurrency}
      </span>
    </div>
  );
};

export default AmountInput;

export type { AmountInputProps };
